import { dateBetweenWithoutYear, dateCompareWithoutYear } from "@omnia/shared/utils/date"
import { INtrReqNutrients, sum } from "../omnia"
import { INutritionCalcService } from "../services/calcService"
import { IClosedPeriodResult, INtrApp, INutritionPlan, INutritionPlanProductApp, INutritionService, NutrientType, NvzPlanCropping } from "../types"
import { sortApplicationDates } from "../utils"
import { INVZMessage, INVZMessages } from "./types"
import { ManureUnit } from "@omnia/shared/generated/enums"
import { pad2 } from "@omnia/shared/utils/utils"

interface IClosedPeriodAppCropping {
    cropping: NvzPlanCropping;
    application: INutritionPlanProductApp;
    closedPeriod: IClosedPeriodResult;
    manure: boolean;
}


export class ClosedPeriodControlService {

    private calcSvc: INutritionCalcService;

    constructor(private svc: INutritionService, private croppingProvider: () => NvzPlanCropping[]) {
        this.calcSvc = svc.calculationService;
    }

    checkClosedPeriods(model: INutritionPlan, croppings: NvzPlanCropping[]): INVZMessages {

        let messages: INVZMessages = {
            error: false,
            canContinue: true,
            messages: []
        };

        let closedPeriods: IClosedPeriodAppCropping[] = [];

        let allCrops = this.croppingProvider();

        model.Applications.filter(a => !!a.ApplicationDate && hasNNutrient(a)).forEach(a => {
            model.Cropping.forEach(c => {

                var cropping = allCrops.find(cr => cr.cropping.CroppingId === c.CroppingId);

                var closedPeriod = this.svc.closedPeriodService.getClosedPeriod(cropping.cropping, this.svc.holding, a.ApplicationDate, a.Manure);
                if (closedPeriod.closed) {
                    closedPeriods.push({
                        closedPeriod: closedPeriod,
                        cropping: cropping,
                        application: a,
                        manure: !!a.Manure
                    });
                }

            });
        });

        if (closedPeriods.length <= 0) {
            return messages;
        }

        closedPeriods.forEach(c => {

            var startDate = c.closedPeriod.specificStartDate ? c.closedPeriod.specificStartDate : c.closedPeriod.startDate;

            var message: INVZMessage = {
                croppingId: c.cropping.croppingId,
                description: "This application date is in the closed period of this cropping ("
                    + getDayMonthDateFormat(startDate) +
                    " to " + getDayMonthDateFormat(c.closedPeriod.endDate) + ").",
                application: c.application
            };

            var addMessage = false;

            // Application date must be between closedPeriod's endDate and end of Febuary
            if (c.closedPeriod.maxRateToMarch &&
                dateCompareWithoutYear(c.application.ApplicationDate, c.closedPeriod.endDate, 7) === 1 &&
                c.application.ApplicationDate.getMonth() < 2) {

                let otherApplications = this.svc.getApplications().filter(a =>
                    (a.CroppingId == c.cropping.croppingId)
                    && (!a.Zone)
                    && (a.PlanId != model.NutritionPlanId)
                    && (a.ManureTypeId === c.application.Manure.ManureTypeId)
                    && (dateCompareWithoutYear(a.ApplicationDate, c.closedPeriod.endDate, 6) >= 0)
                    && (a.ApplicationDate.getMonth() < 2)
                );

                var maxProductRateThreeWeeks = this.getMaxProductRateWithinThreeWeeks(c.application, croppings.find(cr => cr.croppingId === c.cropping.croppingId), otherApplications);

                if (maxProductRateThreeWeeks > c.closedPeriod.maxRateToMarch) {
                    var unit = c.application.Manure.ManureType.Unit === ManureUnit.T ? "t" : "m3";

                    messages.canContinue = false;
                    message.description = "The maximum rate of " + c.application.Manure.Name + " between " +
                        getDayMonthDateFormat(c.closedPeriod.endDate) + " and the end of Febuary can't exceed " +
                        Math.round(c.closedPeriod.maxRateToMarch) + " " + unit + "/ha within a three weeks time frame (actual: " + Math.round(maxProductRateThreeWeeks) + " " + unit + "/ha).";
                    messages.error = true;
                    addMessage = true;
                }
            } else if (c.closedPeriod.maxRate) {

                var maxRateStart = c.closedPeriod.startDate;
                var maxRateEnd = c.closedPeriod.specificStartDate ? c.closedPeriod.specificStartDate : c.closedPeriod.endDate;

                let otherApplications = this.svc.getApplications().filter(a =>
                    (a.CroppingId == c.cropping.croppingId)
                    && (!a.Zone)
                    && (a.PlanId != model.NutritionPlanId)
                    && (dateBetweenWithoutYear(maxRateStart, maxRateEnd, a.ApplicationDate))
                );

                var initialRate = 0;
                if (otherApplications.length > 1) {
                    initialRate = otherApplications.map(s => s.N).reduce((a, b) => a + b);
                } else if (otherApplications.length == 1) {
                    initialRate = otherApplications[0].N;
                }

                var applications = model.Applications
                    .filter(p => p !== c.application && p.ApplicationDate <= c.application.ApplicationDate)
                    .sort((a1, a2) => sortApplicationDates(a1, a2));

                let requirement: INtrReqNutrients = c.cropping.planRequirements.crop

                requirement = this.calcSvc.calculateClosingRequirements(requirement, applications, c.cropping.cropping);

                var rate = initialRate + this.calcSvc.calculateNutrientRateForNutrient(requirement, c.application, NutrientType.N, c.cropping.cropping);

                if (c.closedPeriod.specificStartDate && dateBetweenWithoutYear(c.closedPeriod.specificStartDate, c.closedPeriod.endDate, c.application.ApplicationDate)) {
                    messages.error = true;
                    addMessage = true;
                    /*if (!c.closedPeriod.expemption) {
                        messages.canContinue = false;
                    }*/
                } else if (c.closedPeriod.maxRate < rate) {
                    /*if (!c.closedPeriod.expemption) {
                        messages.canContinue = false;
                    }*/
                    message.description = "The maximum rate between " + getDayMonthDateFormat(maxRateStart) + " and " + getDayMonthDateFormat(maxRateEnd) + " is " + Math.round(c.closedPeriod.maxRate) + "kg of N (actual: " + Math.round(rate) + "kg of N).";
                    messages.error = true;
                    addMessage = true;
                }

            } else {
                messages.error = true;
                addMessage = true;
                /*if (!c.closedPeriod.expemption) {
                    messages.canContinue = false;
                }*/
            }

            if (addMessage) {
                messages.messages.push(message);
                messages.canContinue = (messages.canContinue && (!c.manure));
            }

        });

        return messages;

    }

    getMaxProductRateWithinThreeWeeks(application: INutritionPlanProductApp, cropping: NvzPlanCropping, supplies: INtrApp[]): number {
        var minDate = new Date(application.ApplicationDate.getTime()).addDays(-21);
        var maxDate = new Date(application.ApplicationDate.getTime()).addDays(21);
        var applicationProductRate = this.calcSvc.calculateProductRate(cropping.planRequirements.crop, application, cropping.cropping);
        var productRate = applicationProductRate;

        var filtredSupplies = supplies.filter(s => s.ApplicationDate >= minDate && s.ApplicationDate <= maxDate)
            .sort((a, b) => a.ApplicationDate > b.ApplicationDate ? 1 : a.ApplicationDate < b.ApplicationDate ? -1 : 1);

        filtredSupplies.forEach(s => {
            var maxSupplyDate = new Date(s.ApplicationDate.getTime()).addDays(21);
            if (maxSupplyDate > maxDate) {
                maxSupplyDate = maxDate;
            }

            var amount = Math.round((sum(filtredSupplies.filter(f => f.ApplicationDate >= s.ApplicationDate && f.ApplicationDate <= maxSupplyDate), f => f.ProductRate) + applicationProductRate) * 10) / 10;
            if (amount > productRate) {
                productRate = amount;
            }
        });

        return productRate;
    }

}

function hasNNutrient(app: INutritionPlanProductApp): boolean {
    if (app.Fertiliser) {
        if (app.Fertiliser.Custom) {
            return app.Fertiliser.N > 0;
        }
        return app.Fertiliser.StandardProduct.N > 0;
    } else if (app.Manure) {
        if (app.Manure.Custom) {
            return app.Manure.N > 0;
        }
        return app.Manure.ManureType.N > 0;
    }
    return false;
}

function getDayMonthDateFormat(date: Date) {
    var day = date.getDate();
    var month = date.getMonth() + 1;
    return pad2(day) + "/" + pad2(month);
}
